/**
 * Role-Based Redirect Component
 * Automatically redirects users to their appropriate dashboard based on their role
 */
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardRoute } from '../utils/roleNavigation';
import { shouldRedirectUser, getExpectedDashboardForRole, getUrlForDashboard, getCurrentPort, getPortForDashboard, getPortNames } from '../utils/portUtils';

const RoleBasedRedirect = ({ dashboardType = 'admin' }) => {
  const { user, isLoading, logout } = useAuth();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no user data, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user should be redirected based on port and role
  // Enforce strict port-based access
  if (shouldRedirectUser(user.role, dashboardType)) {
    const expectedDashboard = getExpectedDashboardForRole(user.role);
    const currentPort = getCurrentPort();
    const expectedPort = getPortForDashboard(expectedDashboard);
    
    const portNames = getPortNames();
    
    const currentPortName = portNames[currentPort] || `Port ${currentPort}`;
    const expectedPortName = portNames[expectedPort] || `Port ${expectedPort}`;
    
    // Enforce strict access - redirect to correct port
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-sm text-gray-600 mb-4">
              You are logged in as <strong>{user.role}</strong> but trying to access <strong>{currentPortName}</strong>. 
              {user.role} users must login from <strong>{expectedPortName}</strong> (Port {expectedPort}).
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  // Clear current session and redirect to correct port
                  logout();
                  const redirectUrl = getUrlForDashboard(expectedDashboard, '/login');
                  window.location.href = redirectUrl;
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Go to {expectedPortName}
              </button>
              <button
                onClick={() => {
                  // Logout and go to current port login
                  logout();
                  window.location.href = '/login';
                }}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Login as Different User
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get the appropriate dashboard route based on user role
  const dashboardRoute = getDashboardRoute(user.role, user);
  return <Navigate to={dashboardRoute} replace />;
};

export default RoleBasedRedirect;
